import { analytics } from './firebase';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';

// User journey tracking events
export enum AnalyticsEvent {
  // Page views
  PAGE_VIEW = 'page_view',
  
  // Authentication events
  SIGNUP_STARTED = 'signup_started',
  SIGNUP_COMPLETED = 'signup_completed', 
  LOGIN_STARTED = 'login_started',
  LOGIN_COMPLETED = 'login_completed',
  LOGOUT = 'logout',
  
  // Video processing events
  VIDEO_UPLOAD_STARTED = 'video_upload_started',
  VIDEO_UPLOAD_COMPLETED = 'video_upload_completed',
  VIDEO_UPLOAD_FAILED = 'video_upload_failed',
  VIDEO_PROCESSING_STARTED = 'video_processing_started',
  VIDEO_PROCESSING_COMPLETED = 'video_processing_completed',
  VIDEO_PROCESSING_FAILED = 'video_processing_failed',
  
  // Clip generation events
  CLIP_GENERATION_STARTED = 'clip_generation_started',
  CLIP_GENERATION_COMPLETED = 'clip_generation_completed',
  CLIP_GENERATION_FAILED = 'clip_generation_failed',
  CLIP_DOWNLOADED = 'clip_downloaded',
  CLIP_SHARED = 'clip_shared',
  
  // Subscription events
  SUBSCRIPTION_MODAL_OPENED = 'subscription_modal_opened',
  SUBSCRIPTION_PLAN_VIEWED = 'subscription_plan_viewed',
  SUBSCRIPTION_STARTED = 'subscription_started',
  SUBSCRIPTION_COMPLETED = 'subscription_completed',
  SUBSCRIPTION_CANCELLED = 'subscription_cancelled',
  
  // User limit events
  FREE_LIMIT_REACHED = 'free_limit_reached',
  STORAGE_LIMIT_REACHED = 'storage_limit_reached',
  UPGRADE_PROMPT_SHOWN = 'upgrade_prompt_shown',
  UPGRADE_PROMPT_CLICKED = 'upgrade_prompt_clicked',
  
  // Feature usage
  GUEST_MODE_USED = 'guest_mode_used',
  URL_IMPORT_USED = 'url_import_used',
  SOCIAL_MEDIA_UPLOAD = 'social_media_upload',
  FEEDBACK_SUBMITTED = 'feedback_submitted',
  
  // Drop-off indicators
  UPLOAD_ABANDONED = 'upload_abandoned',
  PROCESSING_ABANDONED = 'processing_abandoned',
  SIGNUP_ABANDONED = 'signup_abandoned',
  CHECKOUT_ABANDONED = 'checkout_abandoned',
  
  // Error events
  API_ERROR = 'api_error',
  UPLOAD_ERROR = 'upload_error',
  AUTHENTICATION_ERROR = 'authentication_error',
  PAYMENT_ERROR = 'payment_error'
}

export interface AnalyticsProperties {
  [key: string]: string | number | boolean | undefined;
}

class AnalyticsService {
  private isInitialized = false;
  private userId: string | null = null;
  
  constructor() {
    this.initialize();
  }
  
  private initialize() {
    if (typeof window !== 'undefined' && analytics) {
      this.isInitialized = true;
      // console.log('🔍 Analytics service initialized');
    }
  }
  
  // Set user identity for tracking
  setUser(userId: string, properties?: AnalyticsProperties) {
    if (!this.isInitialized || !analytics) return;
    
    try {
      this.userId = userId;
      setUserId(analytics, userId);
      
      if (properties) {
        setUserProperties(analytics, properties);
      }
      
      // console.log('👤 Analytics user set:', userId);
    } catch (error) {
      // console.error('Analytics setUser error:', error);
    }
  }
  
  // Track page views
  trackPageView(pageName: string, additionalProps?: AnalyticsProperties) {
    this.trackEvent(AnalyticsEvent.PAGE_VIEW, {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...additionalProps
    });
  }
  
  // Generic event tracking
  trackEvent(eventName: AnalyticsEvent | string, properties?: AnalyticsProperties) {
    if (!this.isInitialized || !analytics) return;
    
    try {
      const eventProperties = {
        timestamp: Date.now(),
        user_id: this.userId,
        ...properties
      };
      
      logEvent(analytics, eventName, eventProperties);
      
      // Log in development for debugging
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('📊 Analytics event:', eventName, eventProperties);
      // }
    } catch (error) {
      // console.error('Analytics tracking error:', error);
    }
  }
  
  // Authentication tracking
  trackSignupStarted(method: 'email' | 'google' = 'email') {
    this.trackEvent(AnalyticsEvent.SIGNUP_STARTED, { method });
  }
  
  trackSignupCompleted(method: 'email' | 'google', userId: string) {
    this.trackEvent(AnalyticsEvent.SIGNUP_COMPLETED, { method, user_id: userId });
  }
  
  trackSignupAbandoned(step: string, method: 'email' | 'google') {
    this.trackEvent(AnalyticsEvent.SIGNUP_ABANDONED, { step, method });
  }
  
  trackLoginStarted(method: 'email' | 'google' = 'email') {
    this.trackEvent(AnalyticsEvent.LOGIN_STARTED, { method });
  }
  
  trackLoginCompleted(method: 'email' | 'google', userId: string) {
    this.trackEvent(AnalyticsEvent.LOGIN_COMPLETED, { method, user_id: userId });
  }
  
  // Video processing funnel tracking
  trackVideoUploadStarted(fileName: string, fileSize: number) {
    this.trackEvent(AnalyticsEvent.VIDEO_UPLOAD_STARTED, {
      file_name: fileName,
      file_size: fileSize,
      file_size_mb: Math.round(fileSize / 1024 / 1024)
    });
  }
  
  trackVideoUploadCompleted(fileName: string, uploadTime: number) {
    this.trackEvent(AnalyticsEvent.VIDEO_UPLOAD_COMPLETED, {
      file_name: fileName,
      upload_time_seconds: Math.round(uploadTime / 1000)
    });
  }
  
  trackVideoUploadFailed(fileName: string, error: string, uploadTime?: number) {
    this.trackEvent(AnalyticsEvent.VIDEO_UPLOAD_FAILED, {
      file_name: fileName,
      error_message: error,
      upload_time_seconds: uploadTime ? Math.round(uploadTime / 1000) : undefined
    });
  }
  
  trackVideoProcessingStarted(videoId: string, duration?: number) {
    this.trackEvent(AnalyticsEvent.VIDEO_PROCESSING_STARTED, {
      video_id: videoId,
      video_duration: duration
    });
  }
  
  trackVideoProcessingCompleted(videoId: string, processingTime: number) {
    this.trackEvent(AnalyticsEvent.VIDEO_PROCESSING_COMPLETED, {
      video_id: videoId,
      processing_time_seconds: Math.round(processingTime / 1000)
    });
  }
  
  trackVideoProcessingFailed(videoId: string, error: string, processingTime?: number) {
    this.trackEvent(AnalyticsEvent.VIDEO_PROCESSING_FAILED, {
      video_id: videoId,
      error_message: error,
      processing_time_seconds: processingTime ? Math.round(processingTime / 1000) : undefined
    });
  }
  
  // Clip generation tracking
  trackClipGenerationStarted(videoId: string, clipType: string) {
    this.trackEvent(AnalyticsEvent.CLIP_GENERATION_STARTED, {
      video_id: videoId,
      clip_type: clipType
    });
  }
  
  trackClipGenerationCompleted(videoId: string, clipId: string, generationTime: number) {
    this.trackEvent(AnalyticsEvent.CLIP_GENERATION_COMPLETED, {
      video_id: videoId,
      clip_id: clipId,
      generation_time_seconds: Math.round(generationTime / 1000)
    });
  }
  
  trackClipDownloaded(videoId: string, clipId: string, format: string) {
    this.trackEvent(AnalyticsEvent.CLIP_DOWNLOADED, {
      video_id: videoId,
      clip_id: clipId,
      format: format
    });
  }
  
  // Subscription funnel tracking
  trackSubscriptionModalOpened(trigger: string, currentPlan: string) {
    this.trackEvent(AnalyticsEvent.SUBSCRIPTION_MODAL_OPENED, {
      trigger: trigger,
      current_plan: currentPlan
    });
  }
  
  trackSubscriptionPlanViewed(planName: string, planPrice: number) {
    this.trackEvent(AnalyticsEvent.SUBSCRIPTION_PLAN_VIEWED, {
      plan_name: planName,
      plan_price: planPrice
    });
  }
  
  trackSubscriptionStarted(planName: string, planPrice: number) {
    this.trackEvent(AnalyticsEvent.SUBSCRIPTION_STARTED, {
      plan_name: planName,
      plan_price: planPrice
    });
  }
  
  trackSubscriptionCompleted(planName: string, planPrice: number, subscriptionId: string) {
    this.trackEvent(AnalyticsEvent.SUBSCRIPTION_COMPLETED, {
      plan_name: planName,
      plan_price: planPrice,
      subscription_id: subscriptionId
    });
  }
  
  // Limit and conversion tracking
  trackFreeLimitReached(limitType: 'videos' | 'storage', currentUsage: number) {
    this.trackEvent(AnalyticsEvent.FREE_LIMIT_REACHED, {
      limit_type: limitType,
      current_usage: currentUsage
    });
  }
  
  trackUpgradePromptShown(context: string, currentPlan: string) {
    this.trackEvent(AnalyticsEvent.UPGRADE_PROMPT_SHOWN, {
      context: context,
      current_plan: currentPlan
    });
  }
  
  trackUpgradePromptClicked(context: string, targetPlan: string) {
    this.trackEvent(AnalyticsEvent.UPGRADE_PROMPT_CLICKED, {
      context: context,
      target_plan: targetPlan
    });
  }
  
  // Feature usage tracking
  trackGuestModeUsed() {
    this.trackEvent(AnalyticsEvent.GUEST_MODE_USED);
  }
  
  trackUrlImportUsed(platform: string) {
    this.trackEvent(AnalyticsEvent.URL_IMPORT_USED, { platform });
  }
  
  trackFeedbackSubmitted(rating: number, category: string) {
    this.trackEvent(AnalyticsEvent.FEEDBACK_SUBMITTED, {
      rating: rating,
      category: category
    });
  }
  
  // Drop-off tracking
  trackUploadAbandoned(step: string, timeSpent: number) {
    this.trackEvent(AnalyticsEvent.UPLOAD_ABANDONED, {
      abandonment_step: step,
      time_spent_seconds: Math.round(timeSpent / 1000)
    });
  }
  
  trackProcessingAbandoned(videoId: string, timeSpent: number) {
    this.trackEvent(AnalyticsEvent.PROCESSING_ABANDONED, {
      video_id: videoId,
      time_spent_seconds: Math.round(timeSpent / 1000)
    });
  }
  
  trackCheckoutAbandoned(planName: string, step: string) {
    this.trackEvent(AnalyticsEvent.CHECKOUT_ABANDONED, {
      plan_name: planName,
      abandonment_step: step
    });
  }
  
  // Error tracking
  trackError(errorType: AnalyticsEvent, errorMessage: string, context?: string) {
    this.trackEvent(errorType, {
      error_message: errorMessage,
      context: context,
      url: window.location.href
    });
  }
  
  // Custom conversion events
  trackConversion(eventName: string, value?: number, currency = 'USD') {
    this.trackEvent(eventName, {
      value: value,
      currency: currency
    });
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
